import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, X, Pencil } from 'lucide-react';
import EditMarketReviewModal from '../../../pages/Admin/MarketReviews/components/EditMarketReviewModal';
import AddMarketReviewModal from '../../../pages/Admin/MarketReviews/components/AddMarketReviewModal';
import EnhancedMarketReviewsTable from './EnhancedMarketReviewsTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedMarketReviews.css';
import { supabase } from '../../../lib/supabase';
import { useMarketReviewFilters } from '../../../hooks/useMarketReviewFilters';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import ExpandableText from '../../../components/common/ExpandableText';
import type { MarketReview, MarketReviewFilter } from '../../../types/market-review';

// Define the sort field type
type SortField = 'review_lot' | 'review_date' | 'review_ucv' | 'review_next';

// Define the sort config type
type SortConfig = {
  field: SortField;
  direction: 'asc' | 'desc';
};

export default function EnhancedMarketReviews() {
  const { canManageSystemSettings, isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  
  // State for modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<MarketReview | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<MarketReviewFilter>({
    lot: null,
    reviewType: null,
    reviewDateAfter: null,
    reviewDateBefore: null,
    nextReviewBefore: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'review_next',
    direction: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Fetch review types to get the descriptions
  const { data: reviewTypes } = useQuery({
    queryKey: ['review_types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('market_review_types')
        .select('review_type, description');
        
      if (error) {
        console.error('Error fetching review types:', error);
        throw error;
      }
      
      return data || [];
    }
  });
  
  // Create a mapping of review type codes to descriptions
  const reviewTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (reviewTypes) {
      reviewTypes.forEach(type => {
        map[type.review_type] = type.description;
      });
    }
    return map;
  }, [reviewTypes]);

  // Fetch market reviews data
  const { data: reviews = [], isLoading, error } = useQuery<MarketReview[]>({
    queryKey: ['market-reviews'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('market_reviews')
          .select('*');

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        return data || [];
      } catch (error) {
        console.error('Error fetching market reviews:', error);
        throw error;
      }
    }
  });

  // Extract unique values for filter dropdowns
  const uniqueLots = useMemo(() => 
    Array.from(new Set(reviews.map(review => review.review_lot))).sort(),
    [reviews]
  );

  const uniqueReviewTypes = useMemo(() => 
    Array.from(new Set(reviews.map(review => review.review_type))).sort(),
    [reviews]
  );

  // Apply filters
  const filteredReviews = useMarketReviewFilters(reviews, searchQuery, filters);

  // Apply sorting
  const sortedReviews = useMemo(() => {
    return [...filteredReviews].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'review_lot': 
          return direction * (a.review_lot || '').localeCompare(b.review_lot || '');
        case 'review_date': 
          return direction * (a.review_date || '').localeCompare(b.review_date || '');
        case 'review_ucv': 
          return direction * ((a.review_ucv || 0) - (b.review_ucv || 0));
        case 'review_next': 
          return direction * (a.review_next || '').localeCompare(b.review_next || '');
        default: 
          return 0;
      }
    });
  }, [filteredReviews, sortConfig]);

  // Apply pagination
  // Add a unique composite key to each review
  const reviewsWithUniqueKeys = useMemo(() => {
    return sortedReviews.map(review => ({
      ...review,
      uniqueKey: `${review.review_lot}_${review.review_date}`
    }));
  }, [sortedReviews]);
  
  const paginatedReviews = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return reviewsWithUniqueKeys.slice(startIndex, endIndex);
  }, [reviewsWithUniqueKeys, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    console.log('Sorting:', sortConfig);
    setSortConfig(sortConfig as SortConfig);
  };

  // Handle sort button clicks
  const handleSortButtonClick = (field: SortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<MarketReviewFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle removing a filter
  const handleRemoveFilter = (key: keyof MarketReviewFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Format date
  const formatTableDate = (dateString: string) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) return dateString;
    return `${day}/${month}/${year}`;
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedReviews.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Handle edit modal
  const handleEditClick = (review: MarketReview) => {
    setSelectedReview(review);
    setIsEditModalOpen(true);
  };

  // Handle edit success
  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['market-reviews'] });
  };

  // Define table columns
  const columns: TableColumn<MarketReview>[] = [
    {
      id: 'actions',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => handleEditClick(row)}
            className="action-button edit"
            title="Edit Market Review"
          >
            <Pencil className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    {
      id: 'review_lot',
      header: 'Lot',
      accessor: (row) => (
        <div className="truncate" title={row.review_lot || 'Not specified'}>
          {formatTextWithLinks(row.review_lot || 'Not specified')}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'review_date',
      header: 'Review Date',
      accessor: (row) => {
        const formattedDate = formatTableDate(row.review_date);
        return (
          <div className="truncate" title={formattedDate}>
            {formattedDate}
          </div>
        );
      },
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'review_type',
      header: 'Type',
      accessor: (row) => {
        const typeText = reviewTypeMap[row.review_type] || row.review_type;
        return (
          <div className="truncate" title={typeText || 'Not specified'}>
            {formatTextWithLinks(typeText || 'Not specified')}
          </div>
        );
      },
      width: 'medium' as ColumnWidth
    },
    {
      id: 'review_ucv',
      header: 'UCV',
      accessor: (row) => {
        const formattedCurrency = formatCurrency(row.review_ucv);
        return (
          <div className="truncate currency-cell" title={formattedCurrency}>
            {formattedCurrency}
          </div>
        );
      },
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'review_next',
      header: 'Next Review',
      accessor: (row) => {
        const formattedDate = formatTableDate(row.review_next);
        return (
          <div className="truncate" title={formattedDate}>
            {formattedDate}
          </div>
        );
      },
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'review_comment',
      header: 'Notes and Docs',
      accessor: (row) => (
        <div className="truncate" title={row.review_comment || 'Not specified'}>
          {formatTextWithLinks(row.review_comment || 'Not specified')}
        </div>
      ),
      width: 'wide' as ColumnWidth
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading market review data. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-market-reviews-container p-4">
      <div className="enhanced-market-reviews-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Market Reviews</h1>
            
            {/* Add button - only visible to users with admin privileges */}
            {(isAdmin || canManageSystemSettings) && (
              <button 
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Market Review
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search market reviews..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Records: {filteredReviews.length}
            </div>
          </div>

          {/* Active filters */}
          {Object.values(filters).some(Boolean) && (
            <div className="active-filters">
              {filters.lot && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Lot:</span>
                  <span>{filters.lot}</span>
                  <button
                    onClick={() => handleRemoveFilter('lot')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.reviewType && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Type:</span>
                  <span>{reviewTypeMap[filters.reviewType] || filters.reviewType}</span>
                  <button
                    onClick={() => handleRemoveFilter('reviewType')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.reviewDateAfter && (
                <div className="filter-tag">
                  <span className="filter-tag-label">After:</span>
                  <span>{formatTableDate(filters.reviewDateAfter)}</span>
                  <button
                    onClick={() => handleRemoveFilter('reviewDateAfter')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.reviewDateBefore && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Before:</span>
                  <span>{formatTableDate(filters.reviewDateBefore)}</span>
                  <button
                    onClick={() => handleRemoveFilter('reviewDateBefore')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.nextReviewBefore && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Next Before:</span>
                  <span>{formatTableDate(filters.nextReviewBefore)}</span>
                  <button
                    onClick={() => handleRemoveFilter('nextReviewBefore')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    lot: null,
                    reviewType: null,
                    reviewDateAfter: null,
                    reviewDateBefore: null,
                    nextReviewBefore: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* Lot filter */}
                <div className="filter-group">
                  <label htmlFor="lot-filter">Lot</label>
                  <select
                    id="lot-filter"
                    value={filters.lot || ''}
                    onChange={(e) => handleFilterChange({ lot: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All lots</option>
                    {uniqueLots.map(lot => (
                      <option key={lot} value={lot}>{lot}</option>
                    ))}
                  </select>
                </div>
                
                {/* Review Type filter */}
                <div className="filter-group">
                  <label htmlFor="review-type-filter">Review Type</label>
                  <select
                    id="review-type-filter"
                    value={filters.reviewType || ''}
                    onChange={(e) => handleFilterChange({ reviewType: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All types</option>
                    {uniqueReviewTypes.map(type => (
                      <option key={type} value={type}>{reviewTypeMap[type] || type}</option>
                    ))}
                  </select>
                </div>
                
                {/* Review Date After filter */}
                <div className="filter-group">
                  <label htmlFor="review-date-after-filter">Review Date After</label>
                  <input
                    id="review-date-after-filter"
                    type="date"
                    value={filters.reviewDateAfter || ''}
                    onChange={(e) => handleFilterChange({ reviewDateAfter: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                {/* Review Date Before filter */}
                <div className="filter-group">
                  <label htmlFor="review-date-before-filter">Review Date Before</label>
                  <input
                    id="review-date-before-filter"
                    type="date"
                    value={filters.reviewDateBefore || ''}
                    onChange={(e) => handleFilterChange({ reviewDateBefore: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                {/* Next Review Before filter */}
                <div className="filter-group">
                  <label htmlFor="next-review-before-filter">Next Review Before</label>
                  <input
                    id="next-review-before-filter"
                    type="date"
                    value={filters.nextReviewBefore || ''}
                    onChange={(e) => handleFilterChange({ nextReviewBefore: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Sort controls removed - now using column headers for sorting */}
        </div>
      </div>
      
      <div className="enhanced-market-reviews-table-container enhanced-table-force-scroll">
        <EnhancedMarketReviewsTable
          columns={columns}
          data={paginatedReviews}
          keyField="uniqueKey"
          width="1800px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage="No market reviews found"
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          permissions={{
            isAdmin,
            canManageSystemSettings
          }}
        />
      </div>

      {/* Add Market Review Modal */}
      {isAddModalOpen && (
        <AddMarketReviewModal
          isOpen={true}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            handleEditSuccess();
            setIsAddModalOpen(false);
          }}
        />
      )}

      {/* Edit Market Review Modal */}
      {selectedReview && (
        <EditMarketReviewModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={handleEditSuccess}
          review={selectedReview}
        />
      )}
    </div>
  );
}
